import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Building2, Upload, DollarSign, BarChart3, Shield, Layers, CheckCircle, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * ForPublishersModal - Informational modal about NewAudia for Publishers
 * Displayed when clicking "Learn More" on the Publishers card on the landing page
 */
const ForPublishersModal = ({ isOpen, onClose, onGetStarted }) => {
  const navigate = useNavigate();
  
  const handleGetStarted = () => {
    onClose(); // Close the modal first
    navigate('/contact/publishers'); // Navigate to publisher contact page
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-900 to-purple-950 border-purple-500/30 text-slate-100">
        <DialogHeader className="border-b border-purple-500/20 pb-4">
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-400" />
            </div>
            NewAudia for Publishers
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Hero Statement */}
          <div className="text-center py-4">
            <h3 className="text-xl font-semibold text-purple-300 mb-2">
              Unlock New Value From Your Catalog
            </h3>
            <p className="text-slate-300">
              Extend the reach of your backlist and frontlist titles with interactive audio — without reallocating production budgets.
            </p>
          </div>

          {/* Key Benefits */}
          <div className="grid gap-4">
            <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">First Manuscript Free Per Author</h4>
                  <p className="text-sm text-slate-400">
                    Your first manuscript upload is free. Additional manuscript uploads are subject to an upload fee per manuscript. No subscriptions required.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Backlist Revival</h4>
                  <p className="text-sm text-slate-400">
                    Give new life to titles that never justified audiobook production. Older works can find new audiences through accessible audio.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Engagement-Based Revenue</h4>
                  <p className="text-sm text-slate-400">
                    Earn commissions based on reader engagement, not retail margins. A new revenue stream that complements — not competes with — existing channels.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Portfolio Analytics</h4>
                  <p className="text-sm text-slate-400">
                    Track engagement across your entire catalog. Understand which titles resonate, identify trends, and make data-driven acquisition decisions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Non-Exclusive, Low Risk</h4>
                  <p className="text-sm text-slate-400">
                    No exclusive contracts. No long-term commitments. Test with a few titles, scale when you see results. Full control over your catalog.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Publisher-Specific Features */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <h4 className="font-semibold text-white mb-4 text-center">Publisher Dashboard Features</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Title management</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Author commission tracking</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Revenue reporting</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Catalog performance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Author management</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span className="text-slate-300">Engagement insights</span>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <h4 className="font-semibold text-white mb-4 text-center">How It Works</h4>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center">
              <div className="flex-1">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">1</div>
                <p className="text-sm text-slate-300">Create publisher account</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 hidden sm:block" />
              <div className="flex-1">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">2</div>
                <p className="text-sm text-slate-300">Upload title EPUBs</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 hidden sm:block" />
              <div className="flex-1">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">3</div>
                <p className="text-sm text-slate-300">Review & approve audio</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 hidden sm:block" />
              <div className="flex-1">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">4</div>
                <p className="text-sm text-slate-300">Track & earn</p>
              </div>
            </div>
          </div>

          {/* FAQ Preview */}
          <div className="space-y-3">
            <h4 className="font-semibold text-white">Common Questions</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400"><span className="text-slate-200">How do author commissions work?</span> — Commissions on reader engagement are automatically calculated and split between you and your authors. NewAudia provides transparent reporting for both parties.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400"><span className="text-slate-200">How do we submit titles?</span> — Books are submitted individually (.epub or .docx) directly to NewAudia. We'll set up your Publisher account and add your Authors and books. <span className="text-purple-300">* This process is being automated to allow full control from your Publisher portal.</span></p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400"><span className="text-slate-200">Does this affect existing audio rights?</span> — No. NewAudia operates independently and doesn't conflict with traditional audiobook agreements.</p>
              </div>
            </div>
          </div>

          {/* Development Notice */}
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4">
            <p className="text-sm text-amber-200 text-center">
              <strong>Note:</strong> These modules are currently in development and will be launched soon. Publishers interested in submitting their books immediately are urged to contact NewAudia today at <span className="font-semibold">(719) 205-6381</span>.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-purple-500/20">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Close
          </Button>
          <Button
            onClick={handleGetStarted}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
            data-testid="publisher-get-started-btn"
          >
            Get Started as a Publisher
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForPublishersModal;
